import { useEffect, useMemo, useState } from "react";
import { STATUS_COLOR, COUNTRY_NOTES, COUNTRY_NEWS, VARIANT_INFO, type CaseStatus, type OutbreakCase, type VariantKey } from "@/data/cases";
import { getFlag, nationalityToCountry } from "@/lib/countryIndex";
import { useCases } from "@/hooks/useCases";
import { useTranslatedTexts } from "@/hooks/useTranslatedTexts";
import { useT } from "@/lib/i18n";
import HVIHeader from "./HVIHeader";
import HVIFooter from "./HVIFooter";
import AISummary from "./AISummary";
import ShareButtons from "./ShareButtons";

// "Today" anchored to the current UTC day. Updates automatically when the
// day rolls over, without a full page reload.
function startOfUtcDay(d: Date) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function utcDayTime(value: string | Date) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return NaN;
  return startOfUtcDay(d).getTime();
}

function caseTimelineDate(c: OutbreakCase) {
  return c.onsetDate ?? c.effectiveDate;
}

function compareCasesNewestFirst(a: OutbreakCase, b: OutbreakCase) {
  const at = caseTimelineDate(a) ? utcDayTime(caseTimelineDate(a) as string) : -Infinity;
  const bt = caseTimelineDate(b) ? utcDayTime(caseTimelineDate(b) as string) : -Infinity;
  return bt - at;
}

export default function HantavirusLiveMap() {
  const { t, lang } = useT();
  const [MapView, setMapView] = useState<React.ComponentType<any> | null>(null);
  const { cases } = useCases();
  const [today, setToday] = useState<Date>(() => startOfUtcDay(new Date()));
  const TODAY = today;
  const [selected, setSelected] = useState<OutbreakCase | null>(null);
  const [flyTarget, setFlyTarget] = useState<{ lat: number; lng: number; zoom?: number } | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [searchQ, setSearchQ] = useState("");
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);
  const [regionsOpen, setRegionsOpen] = useState(false);
  const [variantFilter, setVariantFilter] = useState<VariantKey | "all">("all");
  const [erfTipOpen, setErfTipOpen] = useState(false);
  const [timelinePinnedToNow, setTimelinePinnedToNow] = useState(true);

  // Close ERF tooltip on outside tap (mobile UX)
  useEffect(() => {
    if (!erfTipOpen) return;
    const close = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest("[data-erf-tooltip]")) return;
      setErfTipOpen(false);
    };
    document.addEventListener("click", close);
    document.addEventListener("touchstart", close, { passive: true });
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("touchstart", close);
    };
  }, [erfTipOpen]);

  // Time slider: day 0 = the day BEFORE the earliest onset (so dragging all
  // the way to the left clears the map / counters back to a true "pre-outbreak"
  // zero state). The right edge is always today.
  const dateRange = useMemo(() => {
    const dates = cases
      .map((c) => c.onsetDate ?? c.effectiveDate)
      .filter(Boolean)
      .map((d) => utcDayTime(d as string))
      .filter(Number.isFinite);
    if (dates.length === 0) {
      const max = TODAY.getTime();
      return { min: max - 86400000, max, days: 1 };
    }
    const earliest = Math.min(...dates);
    const min = earliest - 86400000;
    const max = TODAY.getTime();
    return { min, max, days: Math.max(1, Math.ceil((max - min) / 86400000)) };
  }, [cases, TODAY]);
  const [dayOffset, setDayOffset] = useState(dateRange.days);
  // Keep the slider at the latest day on first load / refresh, and continue
  // following "today" unless the user explicitly drags away from it.
  useEffect(() => {
    if (timelinePinnedToNow) setDayOffset(dateRange.days);
  }, [dateRange.days, timelinePinnedToNow]);
  const currentDate = new Date(dateRange.min + dayOffset * 86400000);

  useEffect(() => {
    import("./MapView").then((m) => setMapView(() => m.default));
  }, []);

  // Live UTC clock + automatic day rollover (client-only to avoid SSR mismatch)
  const [nowUtc, setNowUtc] = useState<Date | null>(null);
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setNowUtc(n);
      const sod = startOfUtcDay(n);
      setToday((prev) => (prev.getTime() === sod.getTime() ? prev : sod));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const utcTime = nowUtc ? nowUtc.toISOString().slice(11, 19) + " UTC" : "--:--:-- UTC";

  // Filter cases up to current date + variant filter.
  // A case must have a valid onsetDate <= currentDate to appear; cases with
  // missing onsetDate are excluded from the timeline view (otherwise they'd
  // remain pinned to the map even when the slider is dragged to day 0).
  const visible = useMemo(
    () => cases.filter((c) => {
      const dateStr = c.onsetDate ?? c.effectiveDate;
      if (!dateStr) return false;
      const caseDay = utcDayTime(dateStr);
      if (!Number.isFinite(caseDay) || caseDay > utcDayTime(currentDate)) return false;
      if (variantFilter !== "all" && c.variant !== variantFilter) return false;
      if (!Number.isFinite(c.lat) || !Number.isFinite(c.lng)) return false;
      if (Math.abs(c.lat) < 0.01 && Math.abs(c.lng) < 0.01) return false;
      return true;
    }),
    [cases, currentDate, variantFilter]
  );

  const confirmed = visible.filter((c) => c.status === "confirmed").length;
  const deaths = visible
    .filter((c) => c.status === "confirmed")
    .reduce((sum, c) => sum + (c.deaths ?? 0), 0);
  // Total = every tracked entry on the map (confirmed + suspected + monitoring) + deaths,
  // since fatal cases are counted as additional reported cases in the headline total.
  const total = visible.length + deaths;
  // Suspected = anything not lab-confirmed (suspected + monitoring/contact tracing),
  // so that confirmed + suspected + deaths always equals total.
  const suspected = visible.length - confirmed;
  const countries = new Set(visible.map((c) => c.country)).size;

  // Translate case notes to active language (English passes through unchanged).
  const noteOriginals = useMemo(
    () => Array.from(new Set(visible.map((c) => c.notes).filter(Boolean))),
    [visible],
  );
  const noteTranslated = useTranslatedTexts(noteOriginals, lang);
  const notesMap = useMemo(() => {
    const m = new Map<string, string>();
    noteOriginals.forEach((orig, i) => m.set(orig, noteTranslated[i] ?? orig));
    return m;
  }, [noteOriginals, noteTranslated]);
  const tNote = (s?: string) => (s ? notesMap.get(s) ?? s : "");

  function selectCase(c: OutbreakCase) {
    setSelected(c);
    setFlyTarget({ lat: c.lat, lng: c.lng, zoom: 5 });
    setSelectedCountry(c.country);
  }

  function selectCountry(country: string, lat: number, lng: number) {
    setSelectedCountry(country);
    setSelected(null);
    setFlyTarget({ lat, lng, zoom: 4 });
  }

  const countryStats = Object.entries(
    visible.reduce<Record<string, { cases: number; deaths: number; confirmed: number; suspected: number; monitoring: number; flag: string }>>((acc, c) => {
      if (!acc[c.country]) acc[c.country] = { cases: 0, deaths: 0, confirmed: 0, suspected: 0, monitoring: 0, flag: getFlag(c.country) };
      acc[c.country].cases++;
      acc[c.country].deaths += c.deaths ?? 0;
      if (c.status === "confirmed") acc[c.country].confirmed++;
      else if (c.status === "suspected") acc[c.country].suspected++;
      else if (c.status === "monitoring") acc[c.country].monitoring++;
      return acc;
    }, {})
  ).sort((a, b) => b[1].cases - a[1].cases);

  const filteredStats = countryStats.filter(([c]) => c.toLowerCase().includes(searchQ.toLowerCase()));

  // Country detail data — include cases on-soil AND cases of this country's
  // citizens that occurred elsewhere (so Germany still shows its national
  // who died at sea, etc.).
  const countryDetail = selectedCountry
    ? (() => {
        const onSoil = visible.filter((c) => c.country === selectedCountry);
        const abroad = visible.filter(
          (c) =>
            c.country !== selectedCountry &&
            nationalityToCountry(c.nationality) === selectedCountry,
        );
        const all = [...onSoil, ...abroad].sort(compareCasesNewestFirst);
        return {
          country: selectedCountry,
          flag: getFlag(selectedCountry),
          cases: all,
          abroadCount: abroad.length,
          firstOnset: all
            .filter((c) => c.onsetDate)
            .map((c) => new Date(c.onsetDate!).getTime())
            .sort((a, b) => a - b)[0],
        };
      })()
    : null;

  const dateFmt = (d: Date) => d.toLocaleDateString(lang, { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground font-sans">
      <HVIHeader />

      {/* Live date bar */}
      <div className="bg-[var(--hvi-surface-container-high)] border-b border-border px-4 md:px-6 py-2 flex items-center justify-between text-[11px] gap-3">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-red-400 font-bold uppercase tracking-wider">{t("live")}</span>
          <span className="text-hvi-outline">·</span>
          <span className="font-data text-hvi-on-surface">{dateFmt(currentDate)}</span>
          <span className="text-hvi-outline">·</span>
          <span className="font-data text-hvi-primary tabular-nums">{utcTime}</span>
          {dayOffset < dateRange.days && (
            <button
              onClick={() => {
                setTimelinePinnedToNow(true);
                setDayOffset(dateRange.days);
              }}
              className="ml-2 text-[10px] px-2 py-0.5 border border-border text-hvi-primary hover:bg-card uppercase tracking-wider"
            >
              ⏵ Today
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-hvi-outline font-data">{visible.length}/{cases.length} cases</span>
          <span className="text-hvi-outline">·</span>
          <span className="flex items-center gap-1 text-muted-foreground">
            <span className="text-[10px]">↻</span>
            <span className="font-data">{t("lastSync")}: {nowUtc ? nowUtc.toISOString().slice(11, 16) + " UTC" : "—"}</span>
          </span>
        </div>
      </div>

      <div className="bg-[var(--hvi-error-container)] text-[var(--hvi-error)] px-4 py-1.5 flex items-center justify-between gap-2 text-[11px]">
        <div className="flex items-center gap-2">
          <span>⚠</span>
          <span className="font-bold">{t("alert.active")}</span>
          <span className="hidden md:inline">— {t("alert.detail")}</span>
        </div>
        <span className="relative group" data-erf-tooltip>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setErfTipOpen((v) => !v); }}
            aria-expanded={erfTipOpen}
            aria-describedby="erf-tooltip"
            className="px-2.5 py-1 rounded-full border border-[var(--hvi-error)]/60 bg-[var(--hvi-error)]/10 hover:bg-[var(--hvi-error)]/15 font-semibold uppercase tracking-wider text-[10px] cursor-pointer inline-flex items-center gap-1 transition"
          >
            WHO ERF · Grade 3
            <span className="opacity-60 text-[9px]">ⓘ</span>
          </button>
          <span
            id="erf-tooltip"
            role="tooltip"
            data-erf-tooltip
            className={`absolute right-0 top-full mt-2 z-50 w-72 max-w-[calc(100vw-2rem)] p-3.5 rounded-xl border border-border bg-[var(--hvi-surface-container-high)]/95 backdrop-blur text-foreground text-[11px] leading-relaxed normal-case tracking-normal shadow-xl transition-all duration-200 ${
              erfTipOpen
                ? "opacity-100 translate-y-0 pointer-events-auto"
                : "opacity-0 translate-y-1 pointer-events-none md:group-hover:opacity-100 md:group-hover:translate-y-0 md:group-hover:pointer-events-auto md:group-focus-within:opacity-100 md:group-focus-within:translate-y-0"
            }`}
          >
            <span className="block font-bold text-[var(--hvi-error)] mb-1 uppercase tracking-wider text-[10px]">
              WHO Emergency Response Framework
            </span>
            <span className="block">
              <strong>Grade 3</strong> — Major / complex event with substantial public-health consequences requiring a substantial WHO response across all three levels of the organization.
            </span>
            <span className="block mt-1.5 text-muted-foreground">
              One step below the highest classification: a formal <strong>PHEIC</strong> declaration under IHR (2005).
            </span>
          </span>
        </span>
      </div>

      <main className="flex-1 overflow-y-auto">
        <div className="px-4 md:px-6 py-5 md:py-7">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
            {t("hero.title.l1")}<br />{t("hero.title.l2")}
          </h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-2 max-w-lg">{t("hero.subtitle")}</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="text-[10px] px-2.5 py-1 border border-[#ef4444]/50 bg-[#ef4444]/10 text-[#ef4444] font-bold uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse" />
              ANDV — Andes orthohantavirus
            </span>
          </div>

          {/* Variant filter */}
          <div className="flex flex-wrap items-center gap-1.5 mt-4">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Variant:</span>
            <button
              onClick={() => setVariantFilter("all")}
              className={`text-[10px] px-2.5 py-1 border uppercase tracking-wider transition ${
                variantFilter === "all" ? "border-hvi-primary text-hvi-primary bg-hvi-primary/10" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              All ({cases.length})
            </button>
            {(Object.keys(VARIANT_INFO) as VariantKey[]).map((vk) => {
              const count = cases.filter((c) => c.variant === vk).length;
              if (count === 0) return null;
              const info = VARIANT_INFO[vk];
              return (
                <button
                  key={vk}
                  onClick={() => setVariantFilter(variantFilter === vk ? "all" : vk)}
                  className={`text-[10px] px-2.5 py-1 border uppercase tracking-wider transition flex items-center gap-1 ${
                    variantFilter === vk ? `border-current bg-opacity-10` : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                  style={variantFilter === vk ? { color: info.color, borderColor: info.color, backgroundColor: info.color + "18" } : undefined}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: info.color }} />
                  {vk} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Compact Stats */}
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 px-4 md:px-6 pb-5">
          {[
            { label: t("stats.total"), value: total, border: "border-l-hvi-primary", tip: "Total number of reported hantavirus cases across all statuses." },
            { label: t("stats.deaths"), value: deaths, border: "border-l-[#ef4444]", tip: "Confirmed fatalities from hantavirus infection." },
            { label: t("stats.confirmed"), value: confirmed, border: "border-l-[#f97316]", tip: "Cases confirmed positive via PCR or serological testing." },
            { label: t("stats.suspected"), value: suspected, border: "border-l-[#eab308]", tip: "Cases with matching symptoms, awaiting lab confirmation." },
            { label: t("stats.countries"), value: countries, border: "border-l-[#3b82f6]", tip: "Number of countries with at least one reported case." },
          ].map((s) => (
            <div key={s.label} className={`bg-card border border-border border-l-2 ${s.border} px-2.5 py-2 group relative cursor-help`} title={s.tip}>
              <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground leading-tight">{s.label}</span>
              <div className="font-data text-xl md:text-2xl font-semibold text-foreground mt-0.5 leading-none">{s.value}</div>
              <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-card border border-border text-[11px] text-foreground leading-snug w-48 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity shadow-lg rounded-md">
                {s.tip}
              </div>
            </div>
          ))}
        </div>

        {/* Time slider */}
        <div className="px-4 md:px-6 pb-4">
          <div className="bg-card border border-border px-3 py-2.5">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
              <span>{dateFmt(new Date(dateRange.min))}</span>
              <span className="text-hvi-primary font-bold">{dateFmt(currentDate)}</span>
              <span>{dateFmt(TODAY)}</span>
            </div>
            <input
              type="range"
              aria-label="Timeline date selector"
              min={0}
              max={dateRange.days}
              value={dayOffset}
              onChange={(e) => {
                const next = Number(e.target.value);
                setDayOffset(next);
                setTimelinePinnedToNow(next >= dateRange.days);
              }}
              className="w-full accent-hvi-primary"
            />
          </div>
        </div>

        <div className="px-4 md:px-6 pb-4">
          <div className="label-caps text-muted-foreground mb-2">{t("map.title")}</div>
          <div className="border border-border relative" style={{ height: "70vh", minHeight: "500px" }}>
            {MapView ? (
              <MapView cases={visible} flyTarget={flyTarget} onSelect={selectCase} selectedCountry={selectedCountry} countryStats={Object.fromEntries(countryStats)} onCountryClick={selectCountry} notesMap={notesMap} />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-muted-foreground text-sm bg-background">Loading map…</div>
            )}
            <div className="absolute bottom-3 left-3 z-[1000] bg-card/90 border border-border px-2.5 py-1.5 text-[10px] space-y-1 backdrop-blur">
              {(Object.keys(STATUS_COLOR) as CaseStatus[]).map((s) => (
                <div key={s} className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLOR[s] }} />
                  <span className="text-hvi-on-surface-variant">{t(`status.${s}`)}</span>
                </div>
              ))}
            </div>

            {/* Country detail panel */}
            {countryDetail && !selected && (() => {
              const notes = COUNTRY_NOTES[countryDetail.country];
              const news = COUNTRY_NEWS[countryDetail.country];
              return (
              <div className="absolute top-3 right-3 z-[1000] w-96 max-w-[92vw] max-h-[calc(70vh-1.5rem)] overflow-y-auto bg-card/95 border border-border shadow-2xl backdrop-blur animate-in fade-in slide-in-from-right-2 duration-300">
                <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-border bg-card/95 backdrop-blur">
                  <span className="font-semibold text-sm flex items-center gap-2">
                    <span className="text-base">{countryDetail.flag}</span>
                    {countryDetail.country}
                  </span>
                  <button onClick={() => setSelectedCountry(null)} className="text-muted-foreground hover:text-foreground text-lg leading-none">×</button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Stat color="#ffb690" label={t("stats.total")} value={countryDetail.cases.length} />
                    <Stat color="#ef4444" label={t("stats.deaths")} value={countryDetail.cases.reduce((s, c) => s + (c.deaths ?? 0), 0)} />
                    <Stat color="#f97316" label={t("stats.confirmed")} value={countryDetail.cases.filter(c => c.status === "confirmed").length} />
                    <Stat color="#eab308" label={t("stats.suspected")} value={countryDetail.cases.filter(c => c.status === "suspected").length} />
                  </div>
                  {countryDetail.abroadCount > 0 && (
                    <div className="text-[10px] text-amber-300/90 border border-amber-500/30 bg-amber-500/5 px-2 py-1.5 rounded">
                      {countryDetail.abroadCount} of these {countryDetail.abroadCount === 1 ? "is a national" : "are nationals"} affected abroad (e.g. at sea / in another country).
                    </div>
                  )}
                  {countryDetail.firstOnset && (
                    <div className="text-[10px] text-muted-foreground border-t border-border pt-2">
                      <span className="text-hvi-outline">First case · </span>
                      <span className="font-data">{dateFmt(new Date(countryDetail.firstOnset))}</span>
                    </div>
                  )}

                  {(() => {
                    const variants = countryDetail.cases.reduce<Record<string, number>>((acc, c) => {
                      const v = c.variant || "unknown";
                      acc[v] = (acc[v] || 0) + 1;
                      return acc;
                    }, {});
                    const entries = Object.entries(variants);
                    if (entries.length === 0) return null;
                    return (
                      <div className="border-t border-border pt-2">
                        <div className="label-caps text-muted-foreground mb-1.5">Variant</div>
                        <div className="flex flex-wrap gap-1.5">
                          {entries.map(([vk, n]) => {
                            const info = VARIANT_INFO[vk as VariantKey] || VARIANT_INFO.unknown;
                            return (
                              <span
                                key={vk}
                                title={info.name}
                                className="text-[10px] font-data font-bold px-2 py-0.5 inline-flex items-center gap-1.5"
                                style={{ background: info.color + "20", color: info.color, border: `1px solid ${info.color}40` }}
                              >
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: info.color }} />
                                {vk} · {n}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {notes && (
                    <div className="border-t border-border pt-3 space-y-2">
                      <InfoBlock label="Situation" text={notes.situation} />
                      <InfoBlock label="Response" text={notes.response} />
                      <div className="grid grid-cols-1 gap-2">
                        <InfoBlock label="Strain" text={notes.strain} />
                        <InfoBlock label="Transmission" text={notes.transmission} />
                      </div>
                    </div>
                  )}

                  <AISummary country={countryDetail.country} cases={countryDetail.cases} language={lang} />

                  {news && news.length > 0 && (
                    <div className="border-t border-border pt-3">
                      <div className="label-caps text-muted-foreground mb-2">Latest Updates</div>
                      <div className="space-y-1.5">
                        {news.map((n, ni) => (
                          <div key={ni} className="flex gap-3 text-[11px] border-l-2 border-border pl-3 py-1">
                            <span className="font-data text-muted-foreground whitespace-nowrap">{new Date(n.date).toLocaleDateString("en", { day: "2-digit", month: "short", timeZone: "UTC" })}</span>
                            <div className="flex-1">
                              <p className="text-foreground leading-snug">{n.headline}</p>
                              <span className="text-[10px] text-muted-foreground">{n.source}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="border-t border-border pt-2 space-y-1.5">
                    <div className="label-caps text-muted-foreground mb-1">Cases ({countryDetail.cases.length})</div>
                    {countryDetail.cases.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => selectCase(c)}
                        className="w-full text-left flex items-center gap-2 text-[11px] hover:bg-background/50 px-1.5 py-1 transition"
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: STATUS_COLOR[c.status] }} />
                        <span className="text-hvi-on-surface flex-1 truncate">{c.city || c.nationality || c.country}</span>
                        <span className="text-hvi-outline font-data text-[10px]">
                          {caseTimelineDate(c) ? new Date(caseTimelineDate(c) as string).toLocaleDateString(lang, { day: "2-digit", month: "short", timeZone: "UTC" }) : "—"}
                        </span>
                      </button>
                    ))}
                  </div>
                  <ShareButtons
                    title={`Hantavirus in ${countryDetail.country}: ${countryDetail.cases.length} cases tracked`}
                    text={`Live hantavirus tracker for ${countryDetail.country} — ${countryDetail.cases.length} cases, ${countryDetail.cases.reduce((s, c) => s + (c.deaths ?? 0), 0)} deaths.`}
                    hash="#regions"
                  />
                </div>
              </div>
              );
            })()}


            {selected && (
              <div className="absolute top-3 right-3 z-[1000] w-72 max-w-[85vw] bg-card/95 border border-border shadow-2xl backdrop-blur">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border gap-2">
                  <button
                    onClick={() => setSelected(null)}
                    className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-hvi-primary hover:text-hvi-primary/80 transition"
                    aria-label={countryDetail ? `Back to ${countryDetail.country}` : "Back"}
                  >
                    <span className="text-base leading-none">‹</span>
                    {countryDetail ? countryDetail.country : "Back"}
                  </button>
                  <span className="font-semibold text-sm truncate flex-1 text-right">{selected.city || selected.country}</span>
                  <button onClick={() => { setSelected(null); setSelectedCountry(null); }} className="text-muted-foreground hover:text-foreground text-lg leading-none">×</button>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-xs px-2 py-0.5" style={{ background: STATUS_COLOR[selected.status] + "30", color: STATUS_COLOR[selected.status] }}>
                      {t(`status.${selected.status}`)}
                    </span>
                    <span className="text-xs px-2 py-0.5" style={{ background: VARIANT_INFO[selected.variant].color + "20", color: VARIANT_INFO[selected.variant].color }}>
                      {selected.variant}
                    </span>
                    {selected.onboard && <span className="text-xs px-2 py-0.5 bg-purple-950/60 text-purple-300">MV Hondius</span>}
                  </div>
                  
                  {selected.nationality && <div className="text-xs text-muted-foreground"><span className="text-hvi-outline">{t("detail.nationality")} · </span>{selected.nationality}</div>}
                  {caseTimelineDate(selected) && (
                    <div className="text-xs text-muted-foreground">
                      <span className="text-hvi-outline">{selected.onsetDate ? t("detail.onset") : "Reported"} · </span>
                      {new Date(caseTimelineDate(selected) as string).toLocaleDateString(lang, { timeZone: "UTC" })}
                    </div>
                  )}
                  <p className="text-xs text-foreground leading-relaxed">{tNote(selected.notes)}</p>
                  {selected.source && <p className="text-[10px] text-muted-foreground border-t border-border pt-2">{t("detail.source")}: {selected.source}</p>}
                  <ShareButtons
                    title={`Hantavirus case — ${selected.city || selected.country}`}
                    text={`${t(`status.${selected.status}`)} hantavirus case in ${selected.city || selected.country}.`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div id="regions" className="px-4 md:px-6 pb-6 scroll-mt-4">
          <div className="flex items-center justify-between mb-4 gap-2">
            <button
              onClick={() => setRegionsOpen((v) => !v)}
              aria-expanded={regionsOpen}
              className="group relative flex items-center gap-3 pl-2 pr-4 py-2 rounded-full bg-card/60 backdrop-blur border border-border hover:border-hvi-primary/60 transition-all duration-300 hover:shadow-[0_0_0_4px_color-mix(in_oklab,var(--hvi-primary)_8%,transparent)]"
            >
              <span className="relative flex items-center justify-center w-8 h-8 rounded-full bg-hvi-primary/10 text-hvi-primary border border-hvi-primary/30 group-hover:bg-hvi-primary group-hover:text-background transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18" />
                  <path d="M12 3a14 14 0 0 1 0 18" />
                  <path d="M12 3a14 14 0 0 0 0 18" />
                </svg>
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-hvi-primary animate-pulse" />
              </span>
              <span className="flex flex-col items-start leading-none">
                <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Index</span>
                <span className="text-sm font-semibold text-foreground tracking-tight">{t("regions.title")}</span>
              </span>
              <span className="font-data text-[11px] text-muted-foreground tabular-nums px-2 py-0.5 rounded-full bg-background/60 border border-border ml-1">
                {filteredStats.length}
              </span>
              <svg
                width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                className={`text-muted-foreground transition-transform duration-300 ${regionsOpen ? "rotate-180" : ""}`}
                aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {regionsOpen && (expandedRegion || selectedCountry) && (
              <button
                onClick={() => {
                  setExpandedRegion(null);
                  setSelectedCountry(null);
                  setSelected(null);
                }}
                className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground hover:text-hvi-primary border border-border hover:border-hvi-primary px-2.5 py-1 rounded-full transition flex items-center gap-1"
                title="Undo selection"
              >
                <span>↶</span> Undo
              </button>
            )}
          </div>
          {regionsOpen && (
          <>
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder={t("regions.search")}
            className="w-full bg-background border border-border px-3 py-2 text-xs font-mono text-foreground mb-3 focus:border-hvi-primary outline-none"
          />
          <div className="border border-border">
            <div className="grid grid-cols-[40px_1fr_70px_70px] text-[10px] uppercase tracking-wider font-bold text-muted-foreground px-3 py-2 border-b border-border">
              <span>{t("regions.rank")}</span>
              <span>{t("regions.country")}</span>
              <span className="text-right">{t("regions.cases")}</span>
              <span className="text-right">{t("regions.deaths")}</span>
            </div>
            {filteredStats.map(([country, data], i) => {
              const c = visible.find((x) => x.country === country)!;
              const isSelected = selectedCountry === country;
              const isExpanded = expandedRegion === country;
              const notes = COUNTRY_NOTES[country];
              return (
                <div key={country} className="border-b border-border last:border-b-0">
                  <button
                    onClick={() => {
                      selectCountry(country, c.lat, c.lng);
                      setExpandedRegion(isExpanded ? null : country);
                    }}
                    className={`w-full grid grid-cols-[44px_1fr_80px_80px] px-4 py-4 text-base hover:bg-card/50 active:bg-card/80 transition text-left min-h-[56px] items-center ${isSelected ? "bg-card/70" : ""}`}
                  >
                    <span className="font-data text-muted-foreground">{i + 1}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-lg">{data.flag}</span>
                      <span className={isSelected ? "text-hvi-primary font-semibold" : "text-foreground"}>{country}</span>
                      <span className={`text-sm text-muted-foreground transition-transform ${isExpanded ? "rotate-90" : ""}`}>▸</span>
                    </span>
                    <span className="font-data text-hvi-primary text-right">{data.cases}</span>
                    <span className={`font-data text-right ${data.deaths > 0 ? "text-[#ef4444]" : "text-muted-foreground"}`}>{data.deaths}</span>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-3 pt-1 bg-background/50 animate-fade-in space-y-3">
                      {notes && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <InfoBlock label="Situation" text={notes.situation} />
                          <InfoBlock label="Response" text={notes.response} />
                          <InfoBlock label="Strain" text={notes.strain} />
                          <InfoBlock label="Transmission" text={notes.transmission} />
                        </div>
                      )}
                      <div className="flex gap-2 flex-wrap">
                        {data.confirmed > 0 && <MiniTag color="#f97316" label={`${data.confirmed} Confirmed`} />}
                        {data.suspected > 0 && <MiniTag color="#eab308" label={`${data.suspected} Suspected`} />}
                        {data.monitoring > 0 && <MiniTag color="#3b82f6" label={`${data.monitoring} Monitoring`} />}
                        {data.deaths > 0 && <MiniTag color="#ef4444" label={`${data.deaths} Fatal`} />}
                      </div>
                      {/* News timeline */}
                      {COUNTRY_NEWS[country] && (
                        <div>
                          <div className="label-caps text-muted-foreground mb-2">Latest Updates</div>
                          <div className="space-y-1.5">
                            {COUNTRY_NEWS[country].map((news, ni) => (
                              <div key={ni} className="flex gap-3 text-[11px] border-l-2 border-border pl-3 py-1">
                                <span className="font-data text-muted-foreground whitespace-nowrap">{new Date(news.date).toLocaleDateString("en", { day: "2-digit", month: "short", timeZone: "UTC" })}</span>
                                <div className="flex-1">
                                  <p className="text-foreground leading-snug">{news.headline}</p>
                                  <span className="text-[10px] text-muted-foreground">{news.source}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
          )}
        </div>
      </main>

      
    </div>
  );
}

function Stat({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <div className="border-l-2 px-2 py-1.5 bg-background/50" style={{ borderLeftColor: color }}>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold leading-tight">{label}</div>
      <div className="font-data text-lg font-semibold text-foreground leading-none mt-0.5">{value}</div>
    </div>
  );
}

function InfoBlock({ label, text }: { label: string; text: string }) {
  return (
    <div className="border border-border bg-card p-2.5">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold mb-1">{label}</div>
      <p className="text-[11px] text-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function MiniTag({ color, label }: { color: string; label: string }) {
  return (
    <span className="text-[10px] px-2 py-0.5 font-data font-semibold" style={{ background: color + "20", color }}>
      {label}
    </span>
  );
}
